import React from 'react';
import PropTypes from 'prop-types';

import { HashLink as Link } from 'react-router-hash-link';

import { ResourceTile } from 'components/ResourceTile/ResourceTile';
import { getResourceType } from 'helpers/getResourceType';
import { useClassName } from 'hooks/useClassName';
import { useSettings } from 'hooks/useSettings';

import './_SearchResult.scss';

/**
 * @typedef {object} SearchResultProps
 * @property {string} title Title to display
 * @property {string} icon Icon to display
 * @property {string} backgroundImage Background image to display
 * @property {boolean} gradient Whether to display a gradient over the background image
 * @property {string} url URL to link
 * @property {Function} onSelect Function to call on select
 */

/**
 * @param {SearchResultProps} props SearchResultProps
 * @returns {JSX.Element} SearchResult
 */
const SearchResult = ({
  title = null,
  description = null,
  icon = null,
  backgroundImage = null,
  gradient = true,
  url = '',
  type = '',
  hitType = '',
  onSelect = () => undefined,
}) => {
  const c = useClassName('search-result');
  const { salesCompanionAppId } = useSettings();

  const checkedUrl = url || '#';

  const cardChildren = (
    <div {...c({ small: !backgroundImage })}>
      {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt={`icon: ${title}`} />}
      {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}
      {backgroundImage && <img {...c('background')} src={backgroundImage} alt={`background: ${title}`} />}
      {gradient === true && backgroundImage && <div {...c('gradient')} />}
    </div>
  );

  if (type === 'sales_companion_items') {
    const resourceType = getResourceType({ type: hitType });
    const isHardcodedIframeUrl =
      url?.includes('travpro.yourworldapps.nl/directory/thailand_dir/map') ||
      url?.includes('stack.travpromobile.com/instagram.php?embed_id=');
    const handleSelect = resourceType === 'iframe' || isHardcodedIframeUrl ? undefined : onSelect;

    return (
      <span onClick={handleSelect}>
        <ResourceTile
          type={hitType}
          url={url}
          title={title ?? ''}
          image={backgroundImage ?? ''}
          appId={salesCompanionAppId ?? ''}
          uniqueId={checkedUrl}
          locked={false}
          zoomOnHover={true}
        >
          {cardChildren}
        </ResourceTile>
      </span>
    );
  }

  return (
    <Link to={checkedUrl} onClick={onSelect}>
      <div {...c({ small: !backgroundImage })}>
        {backgroundImage && <img {...c('background')} src={backgroundImage} alt={`background: ${title}`} />}
        {!backgroundImage && <div {...c('background', 'background--fallback')} />}
        {gradient === true && backgroundImage && <div {...c('gradient')} />}

        {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}

        {description && description.props.hit.text.length > 10 && (
          <p {...c('description', { small: !backgroundImage })}>{description}</p>
        )}

        {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt="search-result icon" />}
      </div>
    </Link>
  );
};

export default SearchResult;

// Title and description can be either a string or an element. This is to allow for more complex search results, such as highlighted text, using Algolia Search components
SearchResult.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  icon: PropTypes.string,
  backgroundImage: PropTypes.string,
  gradient: PropTypes.bool,
  url: PropTypes.string,
  onSelect: PropTypes.func,
  type: PropTypes.string,
  hitType: PropTypes.string,
};
